import React from 'react';
import {
  Box,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Checkbox,
  Stepper,
  Step,
  StepLabel,
  FormLabel,
  Button,
  IconButton,
  Grid
} from '@mui/material';
import {
  AttachMoneyRounded as MoneyIcon,
  HotelRounded as HotelIcon,
  FlightRounded as FlightIcon,
  TrainRounded as TrainIcon,
  HomeRounded as HomeIcon,
  BeachAccessRounded as BeachIcon,
  LocationCityRounded as CityIcon,
  MuseumRounded as MuseumIcon,
  RestaurantRounded as RestaurantIcon,
  LocalBarRounded as NightlifeIcon,
  SpaRounded as SpaIcon,
  GroupRounded as FamilyIcon,
  SwapHoriz as SwapIcon
} from '@mui/icons-material';
import LocationAutocomplete from './LocationAutocomplete';

const THEME_COLORS = {
  primary: '#d32f2f',
  secondary: '#f5f5f5',
  accent: '#ff6b35'
};

const PreferencesPanel = ({
  preferences,
  activeStep,
  setActiveStep,
  handlePreferenceChange,
  handleArrayPreferenceChange,
  handleSavePreferences,
  handleLocationSelect,
  swapLocations
}) => {
  const steps = [
    'Trip Basics',
    'Budget & Preferences', 
    'Accommodation & Transport',
    'Restaurant Preferences',
    'Activities & Interests'
  ];

  const tripTypes = [
    { value: 'beach', label: 'Beach', icon: <BeachIcon sx={{ fontSize: 24, color: THEME_COLORS.primary }} /> },
    { value: 'city', label: 'City', icon: <CityIcon sx={{ fontSize: 24, color: THEME_COLORS.primary }} /> },
    { value: 'historical', label: 'Historical', icon: <MuseumIcon sx={{ fontSize: 24, color: THEME_COLORS.primary }} /> },
    { value: 'outdoors', label: 'Outdoors', icon: <BeachIcon sx={{ fontSize: 24, color: THEME_COLORS.primary }} /> },
    { value: 'luxury', label: 'Luxury / Resort', icon: <MoneyIcon sx={{ fontSize: 24, color: THEME_COLORS.primary }} /> },
    { value: 'golf', label: 'Golf', icon: <MoneyIcon sx={{ fontSize: 24, color: THEME_COLORS.primary }} /> },
    { value: 'local', label: 'Local / Domestic', icon: <CityIcon sx={{ fontSize: 24, color: THEME_COLORS.primary }} /> },
    { value: 'international', label: 'International', icon: <FlightIcon sx={{ fontSize: 24, color: THEME_COLORS.primary }} /> }
  ];

  const accommodationStyles = [
    { value: 'budget', label: 'Budget', icon: <HomeIcon sx={{ fontSize: 24, color: THEME_COLORS.primary }} /> },
    { value: 'mid_range', label: 'Mid-range / Boutique', icon: <HotelIcon sx={{ fontSize: 24, color: THEME_COLORS.primary }} /> },
    { value: 'luxury', label: 'Luxury / Resort', icon: <MoneyIcon sx={{ fontSize: 24, color: THEME_COLORS.primary }} /> },
    { value: 'airbnb', label: 'Airbnb / Vacation Rental', icon: <HomeIcon sx={{ fontSize: 24, color: THEME_COLORS.primary }} /> }
  ];

  const transportOptions = [
    { value: 'flights', label: 'Flights', icon: <FlightIcon sx={{ fontSize: 24, color: THEME_COLORS.primary }} /> },
    { value: 'train', label: 'Train', icon: <TrainIcon sx={{ fontSize: 24, color: THEME_COLORS.primary }} /> },
    { value: 'car', label: 'Rental Car', icon: <FlightIcon sx={{ fontSize: 24, color: THEME_COLORS.primary }} /> },
    { value: 'tours', label: 'Guided Tours / Transfers', icon: <TrainIcon sx={{ fontSize: 24, color: THEME_COLORS.primary }} /> }
  ];

  const interests = [
    { value: 'adventure', label: 'Adventure', icon: <BeachIcon sx={{ fontSize: 24, color: THEME_COLORS.primary }} /> },
    { value: 'food', label: 'Food & Culinary', icon: <RestaurantIcon sx={{ fontSize: 24, color: THEME_COLORS.primary }} /> },
    { value: 'culture', label: 'Culture', icon: <MuseumIcon sx={{ fontSize: 24, color: THEME_COLORS.primary }} /> },
    { value: 'shopping', label: 'Shopping', icon: <MoneyIcon sx={{ fontSize: 24, color: THEME_COLORS.primary }} /> },
    { value: 'nightlife', label: 'Nightlife', icon: <NightlifeIcon sx={{ fontSize: 24, color: THEME_COLORS.primary }} /> },
    { value: 'wellness', label: 'Wellness & Spa', icon: <SpaIcon sx={{ fontSize: 24, color: THEME_COLORS.primary }} /> },
    { value: 'family', label: 'Family-Friendly', icon: <FamilyIcon sx={{ fontSize: 24, color: THEME_COLORS.primary }} /> }
  ];

  return (
    <Box sx={{ 
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      gap: 3,
      p: { xs: 1, md: 1.5 }
    }}>
          <Typography variant="h5" sx={{ fontWeight: 600 }}>
            Trip Preferences
          </Typography>

      <Stepper 
        activeStep={activeStep} 
        sx={{ 
          mb: 2, 
          flexWrap: 'wrap',
          gap: 2,
          '& .MuiStepConnector-root': {
            display: 'none'
          },
          '& .MuiStepConnector-line': {
            display: 'none'
          }
        }}
        connector={null}
      >
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>

          {/* Step 1: Trip Basics */}
          {activeStep === 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Trip Basics
                </Typography>
              
                {/* From/To Location Fields */}
                <Grid container spacing={2} alignItems="center">
                  <Grid item xs={12} sm={5}>
                    <LocationAutocomplete
                      value={preferences.fromLocation}
                      onChange={(value) => handlePreferenceChange('fromLocation', value)}
                      onPlaceSelect={(placeData) => handleLocationSelect('fromLocation', placeData)}
                      label="From"
                      placeholder="Departure location"
                      required
                      error={!preferences.fromLocation}
                      helperText={!preferences.fromLocation ? "Departure location is required" : ""}
                    />
                  </Grid>
                  
                  <Grid item xs={12} sm={2} sx={{ display: 'flex', justifyContent: 'center' }}>
                    <IconButton
                      onClick={swapLocations}
                      sx={{
                        border: '1px solid #e0e0e0',
                        borderRadius: '50%',
                        width: 40,
                        height: 40,
                        '&:hover': {
                          backgroundColor: '#f5f5f5',
                        }
                      }}
                    >
                      <SwapIcon />
                    </IconButton>
                  </Grid>
                  
                  <Grid item xs={12} sm={5}>
                    <LocationAutocomplete
                      value={preferences.toLocation}
                      onChange={(value) => handlePreferenceChange('toLocation', value)}
                      onPlaceSelect={(placeData) => handleLocationSelect('toLocation', placeData)}
                      label="To"
                      placeholder="Destination"
                      required
                      error={!preferences.toLocation}
                      helperText={!preferences.toLocation ? "Destination is required" : ""}
                    />
                  </Grid>
                </Grid>

          <Box sx={{ 
            display: 'flex', 
            gap: 2, 
            flexDirection: { xs: 'column', sm: 'row' }
          }}>
                <TextField
                  fullWidth
                  id="start-date"
                  type="date"
                  label="Start Date"
                  value={preferences.startDate}
                  onChange={(e) => handlePreferenceChange('startDate', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  required
                  error={!preferences.startDate}
                  helperText={!preferences.startDate ? "Start date is required" : ""}
                />

                <TextField
                  fullWidth
                  id="end-date"
                  type="date"
                  label="End Date"
                  value={preferences.endDate}
                  onChange={(e) => handlePreferenceChange('endDate', e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  required
                  error={!preferences.endDate}
                  helperText={!preferences.endDate ? "End date is required" : ""}
                />
          </Box>


          <FormControl fullWidth>
            <InputLabel id="travelers-label">Number of Travelers</InputLabel>
            <Select
              labelId="travelers-label"
              id="travelers-select"
              value={preferences.travelers || 1}
              label="Number of Travelers"
              onChange={(e) => handlePreferenceChange('travelers', e.target.value)}
            >
              {[1,2,3,4,5,6,7,8,9,10].map(num => (
                <MenuItem key={num} value={num}>{num}</MenuItem>
              ))}
            </Select>
          </FormControl>

                <FormControlLabel
                  control={
                    <Checkbox
                      checked={preferences.flexibleDates}
                      onChange={(e) => handlePreferenceChange('flexibleDates', e.target.checked)}
                    />
                  }
                  label="My dates are flexible"
                />
        </Box>
          )}

          {/* Step 2: Budget & Preferences */}
          {activeStep === 1 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Budget & Preferences
                </Typography>

                <TextField
                  fullWidth
                  id="budget"
                  label="Budget"
            placeholder="Enter your budget amount"
                  value={preferences.budget}
                  onChange={(e) => handlePreferenceChange('budget', e.target.value)}
                />

                <FormControl fullWidth>
                  <InputLabel id="budget-type-label">Budget Type</InputLabel>
                  <Select
                    labelId="budget-type-label"
                    id="budget-type-select"
              value={preferences.budgetType || 'per_person'}
                    label="Budget Type"
                    onChange={(e) => handlePreferenceChange('budgetType', e.target.value)}
                  >
                    <MenuItem value="per_person">Per Person</MenuItem>
                    <MenuItem value="total">Total Trip</MenuItem>
                  </Select>
                </FormControl>

          <FormControl>
            <FormLabel>Trip Types (select all that apply)</FormLabel>
            <Box sx={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: 1 }}>
              {tripTypes.map((option) => (
                <FormControlLabel
                  key={option.value}
                  control={
                    <Checkbox
                      checked={preferences.tripTypes?.includes(option.value) || false}
                      onChange={(e) => handleArrayPreferenceChange('tripTypes', option.value)}
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {option.icon}
                      {option.label}
                          </Box>
                  }
                />
              ))}
            </Box>
          </FormControl>

          <FormControl fullWidth>
            <InputLabel id="pace-label">Preferred Pace</InputLabel>
            <Select
              labelId="pace-label"
              id="pace-select"
              value={preferences.preferredPace || 'balanced'}
              label="Preferred Pace"
              onChange={(e) => handlePreferenceChange('preferredPace', e.target.value)}
            >
              <MenuItem value="relaxed">Relaxed</MenuItem>
              <MenuItem value="balanced">Balanced</MenuItem>
              <MenuItem value="fast-paced">Fast-paced</MenuItem>
            </Select>
                </FormControl>
        </Box>
          )}

          {/* Step 3: Accommodation & Transport */}
          {activeStep === 2 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Accommodation & Transport
                </Typography>
          
          <FormControl>
            <FormLabel>Accommodation Style (select all that apply)</FormLabel>
            <Box sx={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: 1 }}>
              {accommodationStyles.map((option) => (
                <FormControlLabel
                  key={option.value}
                  control={
                    <Checkbox
                      checked={preferences.accommodationStyles?.includes(option.value) || false}
                      onChange={(e) => handleArrayPreferenceChange('accommodationStyles', option.value)}
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {option.icon}
                      {option.label}
                    </Box>
                  }
                />
              ))}
            </Box>
          </FormControl>

          <FormControl>
            <FormLabel>Transport Preferences (select all that apply)</FormLabel>
            <Box sx={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: 1 }}>
              {transportOptions.map((option) => (
                <FormControlLabel
                  key={option.value}
                  control={
                    <Checkbox
                      checked={preferences.transportPreferences?.includes(option.value) || false}
                      onChange={(e) => handleArrayPreferenceChange('transportPreferences', option.value)}
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {option.icon}
                      {option.label}
                    </Box>
                  }
                />
              ))}
            </Box>
          </FormControl>
        </Box>
          )}

          {/* Step 4: Restaurant Preferences */}
          {activeStep === 3 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Restaurant Preferences
                </Typography>
          
          <FormControl>
            <FormLabel>Cuisine Types (select all that apply)</FormLabel>
            <Box sx={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: 1 }}>
              {[
                { value: 'local', label: 'Local Cuisine' },
                { value: 'international', label: 'International' },
                { value: 'fine-dining', label: 'Fine Dining' },
                { value: 'street-food', label: 'Street Food' },
                { value: 'vegetarian', label: 'Vegetarian' },
                { value: 'vegan', label: 'Vegan' },
                    { value: 'seafood', label: 'Seafood' },
                    { value: 'steakhouse', label: 'Steakhouse' }
              ].map((option) => (
                <FormControlLabel
                  key={option.value}
                  control={
                    <Checkbox
                      checked={preferences.cuisineTypes?.includes(option.value) || false}
                      onChange={(e) => handleArrayPreferenceChange('cuisineTypes', option.value)}
                    />
                  }
                  label={option.label}
                />
              ))}
            </Box>
          </FormControl>

          <TextField
            fullWidth
            id="dietary-restrictions"
            label="Dietary Restrictions"
            placeholder="e.g., Gluten-free, Allergies, etc."
            value={preferences.dietaryRestrictions}
            onChange={(e) => handlePreferenceChange('dietaryRestrictions', e.target.value)}
          />

          <FormControl>
            <FormLabel>Dining Style (select all that apply)</FormLabel>
            <Box sx={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: 1 }}>
              {[
                { value: 'casual', label: 'Casual' },
                { value: 'fine-dining', label: 'Fine Dining' },
                { value: 'family-friendly', label: 'Family-Friendly' },
                { value: 'romantic', label: 'Romantic' },
                { value: 'business', label: 'Business' }
              ].map((option) => (
                <FormControlLabel
                  key={option.value}
                  control={
                    <Checkbox
                      checked={preferences.diningStyle?.includes(option.value) || false}
                      onChange={(e) => handleArrayPreferenceChange('diningStyle', option.value)}
                    />
                  }
                  label={option.label}
                />
              ))}
            </Box>
                </FormControl>

                <FormControl fullWidth>
            <InputLabel id="price-range-label">Price Range</InputLabel>
                  <Select
              labelId="price-range-label"
              id="price-range-select"
              value={preferences.priceRange || ''}
              label="Price Range"
              onChange={(e) => handlePreferenceChange('priceRange', e.target.value)}
            >
                    <MenuItem value="budget">Budget ($)</MenuItem>
                    <MenuItem value="moderate">Moderate ($$)</MenuItem>
                    <MenuItem value="upscale">Upscale ($$$)</MenuItem>
                    <MenuItem value="luxury">Luxury ($$$$)</MenuItem>
                  </Select>
                </FormControl>

                <TextField
                  fullWidth
            id="special-requests"
            label="Special Requests"
            placeholder="Any specific dietary needs or restaurant preferences"
            value={preferences.specialRequests}
            onChange={(e) => handlePreferenceChange('specialRequests', e.target.value)}
          />
        </Box>
          )}

          {/* Step 5: Activities & Interests */}
          {activeStep === 4 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
                  Activities & Interests
                </Typography>
          
          <FormControl>
            <FormLabel>Interests (select all that apply)</FormLabel>
            <Box sx={{ display: 'flex', flexDirection: 'row', flexWrap: 'wrap', gap: 1 }}>
              {interests.map((option) => (
                <FormControlLabel
                  key={option.value}
                  control={
                    <Checkbox
                      checked={preferences.interests?.includes(option.value) || false}
                      onChange={(e) => handleArrayPreferenceChange('interests', option.value)}
                    />
                  }
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {option.icon}
                      {option.label}
                    </Box>
                  }
                />
              ))}
            </Box>
          </FormControl>

                <TextField
                  fullWidth
                  id="must-see-items"
            label="Must-See Items"
            placeholder="e.g., Eiffel Tower, Louvre Museum, specific landmarks"
                  value={preferences.mustSeeItems}
                  onChange={(e) => handlePreferenceChange('mustSeeItems', e.target.value)}
                />
        </Box>
          )}

          {/* Navigation Buttons */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        mt: 4
      }}>
            <Button
          variant="outlined"
          onClick={() => setActiveStep(Math.max(0, activeStep - 1))}
              disabled={activeStep === 0}
          sx={{
            borderColor: THEME_COLORS.primary,
            color: THEME_COLORS.primary,
            '&:hover': {
              borderColor: THEME_COLORS.primary,
              backgroundColor: 'rgba(211, 47, 47, 0.04)',
            }
          }}
            >
              Back
            </Button>

            <Button
              variant="contained"
          onClick={activeStep < steps.length - 1 ? () => setActiveStep(activeStep + 1) : handleSavePreferences}
              sx={{ 
            backgroundColor: THEME_COLORS.primary,
                '&:hover': {
              backgroundColor: THEME_COLORS.primary,
                  transform: 'translateY(-1px)',
                  boxShadow: '0px 4px 8px rgba(0, 0, 0, 0.2)',
                },
                '&:active': {
                  transform: 'translateY(0px)',
                }
              }}
            >
              {activeStep < steps.length - 1 ? 'Next' : 'Save Preferences'}
            </Button>
        </Box>
    </Box>
  );
};

export default PreferencesPanel;